import { useState } from "react";
import { Link } from "react-router-dom";
import api, { useList, errMsg, fmtRM } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, Banknote } from "lucide-react";

export function ApprovalsPage() {
  const { data, loading } = useList("/approvals");

  if (loading || !data) return <Spinner full />;
  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approval Center"
        description="Pending reschedule requests and draft payrolls awaiting decisions. Approvals are conflict-checked and audit-logged." />

      <h3 className="text-lg font-semibold mb-3">Reschedules awaiting final approval ({data.pending_reschedules.length})</h3>
      {data.pending_reschedules.length === 0 ? <EmptyState text="No reschedules awaiting admin approval." /> : (
        <div className="space-y-3 stagger-in">
          {data.pending_reschedules.map((r) => (
            <div key={r.id} className="rounded-lg border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`reschedule-request-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{r.student_name} · {r.course_name} with {r.teacher_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Agreed by both parties: <span className="font-mono">{r.original_date}</span> →{" "}
                  <span className="font-mono">{r.final_agreed_date} {r.final_agreed_start_time}–{r.final_agreed_end_time}</span>
                  {" "}· {r.final_agreed_studio_name}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Requested by {r.requested_by_name} ({r.requested_by_role}) ·
                  quota {r.quota?.month_used}/{r.quota?.month_limit ?? "∞"} this month
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.conflicts?.length > 0
                  ? <StatusBadge status="overdue" testId={`conflict-flag-${r.id}`} />
                  : <StatusBadge status="active" testId={`no-conflict-flag-${r.id}`} />}
                {r.quota_exceeded && <StatusBadge status="pending" />}
                <Button asChild size="sm" data-testid={`review-button-${r.id}`}>
                  <Link to={`/admin/reschedule-approval/${r.id}`}>Review & decide</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.quota_blocked_reschedules?.length > 0 && (
        <>
          <h3 className="text-lg font-semibold mt-10 mb-3">Over-quota requests needing override ({data.quota_blocked_reschedules.length})</h3>
          <div className="space-y-3">
            {data.quota_blocked_reschedules.map((r) => (
              <div key={r.id} className="rounded-lg border border-destructive/40 bg-[#FFEDEC] p-5 flex items-center gap-4" data-testid={`quota-blocked-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{r.student_name} · {r.course_name}</p>
                  <p className="text-xs text-muted-foreground">Requested by {r.requested_by_name} ({r.requested_by_role}) — reschedule limit exceeded</p>
                </div>
                <Button asChild size="sm" variant="outline" data-testid={`override-link-${r.id}`}>
                  <Link to={`/admin/reschedule-approval/${r.id}`}>Review override</Link>
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <h3 className="text-lg font-semibold mt-10 mb-3">Draft payrolls awaiting approval ({data.pending_payrolls.length})</h3>
      {data.pending_payrolls.length === 0 ? <EmptyState text="No draft payrolls." /> : (
        <div className="space-y-3">
          {data.pending_payrolls.map((p) => (
            <div key={p.id} className="rounded-lg border bg-card p-5 flex items-center gap-4" data-testid={`pending-payroll-${p.id}`}>
              <Banknote className="h-5 w-5 text-secondary shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-sm">{p.teacher_name} — {p.payroll_year}-{String(p.payroll_month).padStart(2, "0")}</p>
                <p className="text-xs text-muted-foreground">Net payable {fmtRM(p.net_payable_to_teacher)}</p>
              </div>
              <Button asChild size="sm" variant="outline"><Link to="/admin/payroll/approval">Review in Payroll</Link></Button>
            </div>
          ))}
        </div>
      )}

      <h3 className="text-lg font-semibold mt-10 mb-3">Decision history</h3>
      {data.history.length === 0 ? <EmptyState text="No decisions yet." /> : (
        <div className="rounded-lg border bg-card divide-y">
          {data.history.map((h) => (
            <div key={h.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <StatusBadge status={h.status} />
              <span className="capitalize">{h.request_type}</span>
              <span className="text-xs text-muted-foreground ml-auto">{h.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function EnquiriesPage() {
  const { data, loading, reload } = useList("/enquiries");

  const setStatus = async (id, status) => {
    try {
      await api.put(`/enquiries/${id}`, { status });
      toast.success("Updated");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <Spinner full />;
  return (
    <div data-testid="enquiries-page">
      <PageHeader title="Enquiries & Trial Bookings" description="Submitted from the public website." />
      {!data || data.length === 0 ? <EmptyState text="No enquiries yet." /> : (
        <div className="space-y-3 stagger-in">
          {data.map((e) => (
            <div key={e.id} className="rounded-lg border bg-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm flex items-center gap-2">
                  {e.parent_name}
                  <StatusBadge status={e.enquiry_type === "trial" ? "pending" : "new"} />
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {e.phone} {e.email && `· ${e.email}`} · Student: {e.student_name || "—"} · Interested in {e.course_interest || "—"}
                  {e.preferred_time && ` · Prefers ${e.preferred_time}`}
                </p>
                {e.message && <p className="text-xs mt-1">{e.message}</p>}
              </div>
              <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                <SelectTrigger className="w-36 shrink-0" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
